import React, { useState, useEffect } from 'react';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';
import apiService from '../../services/api';
import { ClinicUserApiResponse } from '../../types/api';

interface EditUserModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    token: string;
    user: ClinicUserApiResponse | null;
}

export const EditUserModal: React.FC<EditUserModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    token,
    user,
}) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [admin, setAdmin] = useState('no');
    const [data, setData] = useState('test');
    const [type, setType] = useState('doctor');
    const [hebName, setHebName] = useState('');
    const [license, setLicense] = useState('');
    const [tz, setTz] = useState('');
    const [signLink, setSignLink] = useState('');
    const [yomanId, setYomanId] = useState('');
    const [phone, setPhone] = useState('');
    const [phoneExt, setPhoneExt] = useState('');
    const [updateInClicksUserId, setUpdateInClicksUserId] = useState(1);
    const [showPrepMeeting, setShowPrepMeeting] = useState(true);
    const [updateSummaryInClicks, setUpdateSummaryInClicks] = useState(true);
    const [showClicksTasks, setShowClicksTasks] = useState(true);
    const [manageUsers, setManageUsers] = useState(false);
    const [active, setActive] = useState(true);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [users, setUsers] = useState<Array<{ id: number; name: string }>>([]);

    // Populate form when user changes
    useEffect(() => {
        if (user) {
            setName(user.name || '');
            setEmail(user.email || '');
            setAdmin(user.role === 'Admin' || user.role === 'admin' ? 'yes' : 'no');
            setData(user.data || 'test');
            // Convert 'secretariat' from API to 'secretary' for the UI
            const userType = user.type === 'secretariat' ? 'secretary' : user.type;
            setType(userType || 'doctor');
            setHebName(user.heb_name || '');
            setLicense(user.license || '');
            setTz(user.doc_id || '');
            setSignLink(user.sign_link || '');
            setYomanId(user.yoman_id || '');
            setPhone(user.phone || '');
            setPhoneExt(user.phone_ext || '');
            setUpdateInClicksUserId(user.update_in_clicks_user_id || 1);
            setShowPrepMeeting(user.show_prep_meeting ?? true);
            setUpdateSummaryInClicks(user.update_summary_in_clicks ?? true);
            setShowClicksTasks(user.show_clicks_tasks ?? true);
            setManageUsers(user.manage_users ?? false);
            setActive(user.active ?? true);
        }
    }, [user]);

    // Fetch users for the dropdown
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await apiService.getUsers({
                    token,
                    user_type: 'all',
                    institution_id: 1,
                });
                // Filter for active users with type case_manager and institution_id = 1
                const filteredUsers = response.filter(u => u.active === true && u.type === 'case_manager' && u.institution_id === 1);
                setUsers(filteredUsers.map(u => ({ id: u.id, name: u.name })));
            } catch (err) {
                console.error('Failed to fetch users:', err);
            }
        };
        if (isOpen) {
            fetchUsers();
        }
    }, [isOpen, token]);

    const handleSubmit = async () => {
        console.log('[EditUserModal] handleSubmit called');

        if (!user) return;

        // Basic validation
        if (!name || !email || !hebName) {
            setError('Name, email, and Hebrew name are required');
            return;
        }

        // Phone validation for non-secretary users
        if (type !== 'secretary' && !phone) {
            setError('Phone is required for non-secretary users');
            return;
        }

        // Yoman ID validation for doctors and case managers
        if ((type === 'doctor' || type === 'case_manager') && !yomanId) {
            setError('Yoman ID is required for doctors and case managers');
            return;
        }

        // ID Number validation for case managers
        if (type === 'case_manager' && !tz) {
            setError('ID Number (TZ) is required for case managers');
            return;
        }

        // License, ID Number, and Sign Link validation for doctors
        if (type === 'doctor') {
            if (!license) {
                setError('License is required for doctors');
                return;
            }
            if (!tz) {
                setError('ID Number (TZ) is required for doctors');
                return;
            }
            if (!signLink) {
                setError('Sign Link is required for doctors');
                return;
            }
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('Please enter a valid email address');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            const response = await apiService.updateUser({
                token,
                id: user.id,
                active,
                name,
                email,
                admin,
                data,
                type: type === 'secretary' ? 'secretariat' : type, // Map secretary to secretariat for API
                heb_name: hebName,
                license,
                TZ: tz,
                sign_link: signLink,
                yoman_id: yomanId,
                phone,
                phone_ext: phoneExt,
                update_in_clicks_user_id: updateInClicksUserId,
                show_prep_meeting: showPrepMeeting,
                update_summary_in_clicks: updateSummaryInClicks,
                show_clicks_tasks: showClicksTasks,
                manage_users: manageUsers,
            });

            // Check if the response contains an error field (backend returns 200 with error message)
            if ((response as any).error) {
                console.error('[EditUserModal] API returned error in response:', (response as any).error);
                setError((response as any).error);
                setIsSubmitting(false);
                return; // Don't call onSuccess or onClose
            }

            // Call success callback and close
            onSuccess();
            onClose();
        } catch (err: any) {
            console.error('Failed to update user:', err);
            console.error('[EditUserModal] Error response:', err.response?.data);
            const errorMessage = err.response?.data?.detail || err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to update user';
            console.error('[EditUserModal] Setting error message:', errorMessage);
            setError(errorMessage);
            // Do NOT call onSuccess or onClose here - let the user see the error
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setError(null);
            onClose();
        }
    };

    if (!user) return null;

    return (
        <ModalWrapper modalId="EditUserModal"
            isOpen={isOpen}
            onClose={handleClose}
            title="Edit User"
            isLoading={isSubmitting}
            width="max-w-2xl"
        >
            <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                {/* Active Status */}
                <div className="bg-[#EBF5F5] p-4 rounded-lg space-y-3">
                    <h3 className="text-sm font-semibold text-[#366A75] mb-2">Status</h3>
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={active}
                            onChange={(e) => setActive(e.target.checked)}
                            className="rounded border-[#B4D9D4]"
                            disabled={isSubmitting}
                        />
                        <span className="text-sm text-gray-700">Active</span>
                    </label>
                </div>

                {/* Basic Information */}
                <div className="bg-[#EBF5F5] p-4 rounded-lg space-y-3">
                    <h3 className="text-sm font-semibold text-[#366A75] mb-2">Basic Information</h3>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-[#366A75] mb-1">
                                Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className={POPUP_STYLES.input}
                                disabled={isSubmitting}
                                placeholder="Enter name"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-[#366A75] mb-1">
                                Hebrew Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={hebName}
                                onChange={(e) => setHebName(e.target.value)}
                                className={POPUP_STYLES.input}
                                disabled={isSubmitting}
                                placeholder="Enter Hebrew name"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[#366A75] mb-1">
                            Email <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className={POPUP_STYLES.input}
                            disabled={isSubmitting}
                            placeholder="user@example.com"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[#366A75] mb-1">
                            Phone {type !== 'secretary' && <span className="text-red-500">*</span>}
                        </label>
                        <input
                            type="text"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            className={POPUP_STYLES.input}
                            disabled={isSubmitting}
                            placeholder="555-1234"
                        />
                    </div>
                </div>

                {/* Role & Type */}
                <div className="bg-[#EBF5F5] p-4 rounded-lg space-y-3">
                    <h3 className="text-sm font-semibold text-[#366A75] mb-2">Role & Type</h3>

                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-[#366A75] mb-1">
                                Type
                            </label>
                            <select
                                value={type}
                                onChange={(e) => setType(e.target.value)}
                                className={POPUP_STYLES.input}
                                disabled={isSubmitting}
                            >
                                <option value="doctor">Doctor</option>
                                <option value="case_manager">Case Manager</option>
                                <option value="secretary">Secretary</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-[#366A75] mb-1">
                                See All Patients
                            </label>
                            <select
                                value={admin}
                                onChange={(e) => setAdmin(e.target.value)}
                                className={POPUP_STYLES.input}
                                disabled={isSubmitting}
                            >
                                <option value="no">No</option>
                                <option value="yes">Yes</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-[#366A75] mb-1">
                                Environment
                            </label>
                            <select
                                value={data}
                                onChange={(e) => setData(e.target.value)}
                                className={POPUP_STYLES.input}
                                disabled={isSubmitting}
                            >
                                <option value="test">Test</option>
                                <option value="prod">Production</option>
                                <option value="all">All</option>
                            </select>
                        </div>
                    </div>
                </div>


                {/* Info - shown for all types */}
                {type === 'secretary' ? (
                    <div className="bg-[#EBF5F5] p-4 rounded-lg space-y-3">
                        <h3 className="text-sm font-semibold text-[#366A75] mb-2">Info</h3>
                        <div>
                            <label className="block text-sm font-medium text-[#366A75] mb-1">
                                Phone Extension
                            </label>
                            <input
                                type="text"
                                value={phoneExt}
                                onChange={(e) => setPhoneExt(e.target.value)}
                                className={POPUP_STYLES.input}
                                disabled={isSubmitting}
                                placeholder="101"
                            />
                        </div>
                    </div>
                ) : (
                    <div className="bg-[#EBF5F5] p-4 rounded-lg space-y-3">
                        <h3 className="text-sm font-semibold text-[#366A75] mb-2">Info</h3>

                        {type === 'doctor' && (
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-[#366A75] mb-1">
                                        License <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={license}
                                        onChange={(e) => setLicense(e.target.value)}
                                        className={POPUP_STYLES.input}
                                        disabled={isSubmitting}
                                        placeholder="123456"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-[#366A75] mb-1">
                                        ID Number (TZ) <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={tz}
                                        onChange={(e) => setTz(e.target.value)}
                                        className={POPUP_STYLES.input}
                                        disabled={isSubmitting}
                                        placeholder="987654321"
                                    />
                                </div>
                            </div>
                        )}

                        {type === 'case_manager' && (
                            <div>
                                <label className="block text-sm font-medium text-[#366A75] mb-1">
                                    ID Number (TZ) <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={tz}
                                    onChange={(e) => setTz(e.target.value)}
                                    className={POPUP_STYLES.input}
                                    disabled={isSubmitting}
                                    placeholder="987654321"
                                />
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-[#366A75] mb-1">
                                Yoman ID {(type === 'doctor' || type === 'case_manager') && <span className="text-red-500">*</span>}
                            </label>
                            <input
                                type="text"
                                value={yomanId}
                                onChange={(e) => setYomanId(e.target.value)}
                                className={POPUP_STYLES.input}
                                disabled={isSubmitting}
                                placeholder="yoman123"
                            />
                        </div>

                        {type === 'doctor' && (
                            <div>
                                <label className="block text-sm font-medium text-[#366A75] mb-1">
                                    Sign Link <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={signLink}
                                    onChange={(e) => setSignLink(e.target.value)}
                                    className={POPUP_STYLES.input}
                                    disabled={isSubmitting}
                                    placeholder="http://example.com/sign"
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Clicks - Only shown for Doctor type */}
                {type === 'doctor' && (
                    <div className="bg-[#EBF5F5] p-4 rounded-lg space-y-3">
                        <h3 className="text-sm font-semibold text-[#366A75] mb-2">Clicks</h3>

                        <div className="space-y-3">
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={updateSummaryInClicks}
                                    onChange={(e) => setUpdateSummaryInClicks(e.target.checked)}
                                    className="rounded border-[#B4D9D4]"
                                    disabled={isSubmitting}
                                />
                                <span className="text-sm text-gray-700">Update Summary in Clicks</span>
                            </label>

                            {/* Update In Clicks dropdown - hidden if Update Summary is checked */}
                            {!updateSummaryInClicks && (
                                <div>
                                    <label className="block text-sm font-medium text-[#366A75] mb-1">
                                        Update In Clicks
                                    </label>
                                    <select
                                        value={updateInClicksUserId}
                                        onChange={(e) => setUpdateInClicksUserId(parseInt(e.target.value) || 1)}
                                        className={POPUP_STYLES.input}
                                        disabled={isSubmitting}
                                    >
                                        {users.map(user => (
                                            <option key={user.id} value={user.id}>
                                                {user.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Permissions */}
                <div className="bg-[#EBF5F5] p-4 rounded-lg space-y-3">
                    <h3 className="text-sm font-semibold text-[#366A75] mb-2">Permissions</h3>

                    <div className="grid grid-cols-2 gap-3">
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={showPrepMeeting}
                                onChange={(e) => setShowPrepMeeting(e.target.checked)}
                                className="rounded border-[#B4D9D4]"
                                disabled={isSubmitting}
                            />
                            <span className="text-sm text-gray-700">Show Prep Meeting</span>
                        </label>

                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={showClicksTasks}
                                onChange={(e) => setShowClicksTasks(e.target.checked)}
                                className="rounded border-[#B4D9D4]"
                                disabled={isSubmitting}
                            />
                            <span className="text-sm text-gray-700">Show Clicks Tasks</span>
                        </label>

                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={manageUsers}
                                onChange={(e) => setManageUsers(e.target.checked)}
                                className="rounded border-[#B4D9D4]"
                                disabled={isSubmitting}
                            />
                            <span className="text-sm text-gray-700">Manage Users</span>
                        </label>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                        {error}
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 justify-end pt-2">
                    <button
                        onClick={handleClose}
                        className={POPUP_STYLES.button}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        className={POPUP_STYLES.button}
                        disabled={isSubmitting || !name || !email || !hebName || (type !== 'secretary' && !phone) || ((type === 'doctor' || type === 'case_manager') && !yomanId) || (type === 'case_manager' && !tz) || (type === 'doctor' && (!license || !tz || !signLink))}
                    >
                        {isSubmitting ? 'Updating...' : 'Update User'}
                    </button>
                </div>
            </div>
        </ModalWrapper>
    );
};
